/**
 * framework-trend.ts — per-framework trend helpers shared by the Security
 * Center Overview tab and the new BPA framework cards.
 */

import type { ComplianceTrendPoint } from '@/types/standardsCompliance'

/** UTC calendar-day key (YYYY-MM-DD) for a trend point's ISO date. */
function dayKey(isoDate: string): string {
  return new Date(isoDate).toISOString().slice(0, 10)
}

/**
 * Compute the 7-day score delta for one framework from the trend-point series.
 * Falls back to `null` when the history is too short to compare honestly —
 * mirroring the PillarBpa 7d trend behavior.
 */
export function compute7dFrameworkDelta(
  history: ComplianceTrendPoint[],
  frameworkCode: string,
  currentScore: number,
): number | null {
  if (!history || history.length === 0) return null

  const sevenDaysAgo = Date.now() - 7 * 24 * 60 * 60 * 1000
  const sorted = [...history].sort(
    (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
  )

  // Prefer the most recent point that is at least 7 days old.
  let baseline: number | null = null
  for (const point of sorted) {
    if (new Date(point.date).getTime() <= sevenDaysAgo) {
      const fw = point.frameworkScores.find(f => f.code === frameworkCode)
      if (fw) baseline = fw.score
    } else {
      break
    }
  }

  // Fall back to the oldest point we have — but ONLY if it's from an earlier
  // calendar day than the latest point. A brand-new tenant whose only history
  // is today's (often several intraday evaluations) must NOT show a 7d trend:
  // comparing today's live score against today's stored snapshot manufactures a
  // delta out of formula/timing noise, not real movement. Return null in that
  // case so the card shows "— 7d" until there's a genuine prior day to compare.
  if (baseline === null) {
    const latestDay = dayKey(sorted[sorted.length - 1].date)
    for (const point of sorted) {
      if (dayKey(point.date) >= latestDay) break
      const fw = point.frameworkScores.find(f => f.code === frameworkCode)
      if (fw) {
        baseline = fw.score
        break
      }
    }
  }

  if (baseline === null) return null
  return Math.round(currentScore - baseline)
}

/**
 * Pivot a ComplianceTrendPoint[] into a single-framework score series for the
 * inline SVG sparklines on the BPA framework cards. Sorted ascending by date,
 * nulls dropped (the sparkline draws over only points where we have a score).
 */
export function frameworkSparkline(
  history: ComplianceTrendPoint[],
  code: string,
): number[] {
  if (!history || history.length === 0) return []
  return history
    .slice()
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
    .map(pt => pt.frameworkScores.find(f => f.code === code)?.score ?? null)
    .filter((v): v is number => v !== null)
}
